import { ReviewPreset } from '../types/review-preset.type'
import { ReviewPresetEntry } from '../types/reviewPresetEntry'
import { Timeframe } from '../types/timeframe.type'

class MedicalBackgroundPreset {
  preset: ReviewPreset.MEDICAL_BACKGROUND
  conditionName: string
  alternativeConditionNames: string[]
  targetLoe: string
  timeRange: Timeframe | undefined
  timeLimitFromDate?: string
  timeLimitToDate?: string
  purpose: string
  isAutoScreeningPaused: boolean
  isAutoExtractionPaused: boolean

  constructor(data: Omit<MedicalBackgroundPreset, 'preset'>) {
    this.preset = ReviewPreset.MEDICAL_BACKGROUND
    this.conditionName = data.conditionName
    this.alternativeConditionNames = data.alternativeConditionNames
    this.targetLoe = data.targetLoe
    this.timeRange = data.timeRange
    this.timeLimitFromDate = data.timeLimitFromDate
    this.timeLimitToDate = data.timeLimitToDate
    this.purpose = data.purpose
    this.isAutoScreeningPaused = data.isAutoScreeningPaused
    this.isAutoExtractionPaused = data.isAutoExtractionPaused
  }
}

class NotifiedBodyPreset {
  preset: ReviewPreset.NOTIFIED_BODY
  deviceGroup: string
  alternativeDeviceGroupNames: string[]
  targetLoe: string
  timeRange: Timeframe | undefined
  timeLimitFromDate?: string
  timeLimitToDate?: string
  isAutoScreeningPaused: boolean
  isAutoExtractionPaused: boolean

  constructor(data: Omit<NotifiedBodyPreset, 'preset'>) {
    this.preset = ReviewPreset.NOTIFIED_BODY
    this.deviceGroup = data.deviceGroup
    this.alternativeDeviceGroupNames = data.alternativeDeviceGroupNames
    this.targetLoe = data.targetLoe
    this.timeRange = data.timeRange
    this.timeLimitFromDate = data.timeLimitFromDate
    this.timeLimitToDate = data.timeLimitToDate
    this.isAutoScreeningPaused = data.isAutoScreeningPaused
    this.isAutoExtractionPaused = data.isAutoExtractionPaused
  }
}

class DeviceSpecificSearchPreset {
  preset: ReviewPreset.DEVICE_SPECIFIC_SEARCH
  deviceName: string
  manufacturerName: string
  alternativeDeviceNames: string[]
  alternativeManufacturerNames: string[]
  isAutoScreeningPaused: boolean
  isAutoExtractionPaused: boolean

  constructor(data: {
    deviceName: string
    manufacturerName: string
    alternativeDeviceNames: string[]
    alternativeManufacturerNames: string[]
    isAutoScreeningPaused: boolean
    isAutoExtractionPaused: boolean
  }) {
    this.preset = ReviewPreset.DEVICE_SPECIFIC_SEARCH
    this.deviceName = data.deviceName
    this.manufacturerName = data.manufacturerName
    this.alternativeDeviceNames = data.alternativeDeviceNames
    this.alternativeManufacturerNames = data.alternativeManufacturerNames
    this.isAutoScreeningPaused = data.isAutoScreeningPaused
    this.isAutoExtractionPaused = data.isAutoExtractionPaused
  }
}

class DefaultPreset {
  preset: ReviewPreset.DEFAULT
  isAutoScreeningPaused: boolean
  isAutoExtractionPaused: boolean

  constructor() {
    this.preset = ReviewPreset.DEFAULT
    this.isAutoScreeningPaused = true
    this.isAutoExtractionPaused = true
  }
}

export class ReviewPresetFactory {
  static createPreset(data: ReviewPresetEntry) {
    switch (data.preset) {
      case ReviewPreset.MEDICAL_BACKGROUND:
        return new MedicalBackgroundPreset({
          alternativeConditionNames: data.alternativeConditionNames,
          conditionName: data.conditionName,
          targetLoe: data.targetLoe,
          timeRange: data.timeRange,
          timeLimitFromDate: data.timeLimitFromDate,
          timeLimitToDate: data.timeLimitToDate,
          purpose: data.purpose,
          isAutoScreeningPaused: data.isAutoScreeningPaused,
          isAutoExtractionPaused: data.isAutoExtractionPaused,
        })

      case ReviewPreset.DEVICE_SPECIFIC_SEARCH:
        return new DeviceSpecificSearchPreset({
          deviceName: data.deviceName,
          manufacturerName: data.manufacturerName,
          alternativeDeviceNames: data.alternativeDeviceNames,
          alternativeManufacturerNames: data.alternativeManufacturerNames,
          isAutoScreeningPaused: data.isAutoScreeningPaused,
          isAutoExtractionPaused: data.isAutoExtractionPaused,
        })

      case ReviewPreset.NOTIFIED_BODY:
        return new NotifiedBodyPreset({
          deviceGroup: data.deviceGroup,
          alternativeDeviceGroupNames: data.alternativeDeviceGroupNames,
          targetLoe: data.targetLoe,
          timeRange: data.timeRange,
          timeLimitFromDate: data.timeLimitFromDate,
          timeLimitToDate: data.timeLimitToDate,
          isAutoScreeningPaused: data.isAutoScreeningPaused,
          isAutoExtractionPaused: data.isAutoExtractionPaused,
        })
      case ReviewPreset.DEFAULT:
      case ReviewPreset.CUSTOM:
      default:
        return new DefaultPreset()
    }
  }
}
